import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Button,
    Grid,
    Row,
    Col,
    Glyphicon,
    FormGroup,
    FormControl,
    Label,
    Table
} from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { getCapsAdminOnly, patchCapUpdates } from "../utils/bedrockAPI";
import { getUser } from "../utils/Common";
import "./CapSoftware.css";
import "./Admin.css";

function formatDate(d) {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleString();
}

function formatDateOnly(d) {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString();
}

const AMERICA_TZ_PREFIX = "America/";

/** IANA timezone for table display: drop leading America/ when present; otherwise full id. */
function formatTimezoneDisplay(tz) {
    if (tz == null) return null;
    const s = String(tz).trim();
    if (!s) return null;
    const rest = s.startsWith(AMERICA_TZ_PREFIX) ? s.slice(AMERICA_TZ_PREFIX.length) : s;
    return rest || null;
}

/** True if value looks like NANP 10-digit or 1+10 (same digit rules as formatPhoneNumber in Common.js). */
function looksLikePhoneNumber(value) {
    if (value == null || value === "") return false;
    const cleaned = String(value).replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
        return /^1\d{10}$/.test(cleaned);
    }
    if (cleaned.length === 10) {
        return /^\d{10}$/.test(cleaned);
    }
    return false;
}

/** Format now as YYYY-MM-DD HH:MM:SS.ffffff for daemon compatibility (use .fff000 for microseconds). */
function formatUpdateAvailableDate() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const h = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const s = String(now.getSeconds()).padStart(2, "0");
    const ms = String(now.getMilliseconds()).padStart(3, "0");
    return `${y}-${m}-${d} ${h}:${min}:${s}.${ms}000`;
}

function getSystemType(architecture) {
    if (!architecture) return "Legacy";
    if (architecture.includes("2CAM")) return "2 cam";
    if (architecture.includes("4CAM")) return "4 cam";
    return "Legacy";
}

function getStatusBsStyle(status) {
    if (status === "FAILURE") return "danger";
    if (status === "UPDATE_AVAILABLE") return "warning";
    if (status === "UPDATED") return "success";
    return "default";
}

function getHealthBsStyle(health) {
    if (health === "ONLINE") return "success";
    if (health === "BATTERY") return "warning";
    if (health === "POWER_LOSS" || health === "ERROR") return "danger";
    return "default";
}

/** Returns { text: "Updated"|"Pending"|"—", bsStyle: "success"|"warning"|"default" } for HTTPS keys. Updated when last_updated >= update_available. */
function getHttpsKeysDisplay(httpsKeys) {
    if (!httpsKeys || typeof httpsKeys !== "object") return { text: "—", bsStyle: "default" };
    const lastUpdated = httpsKeys.last_updated ? new Date(httpsKeys.last_updated).getTime() : 0;
    const updateAvailable = httpsKeys.update_available ? new Date(httpsKeys.update_available).getTime() : 0;
    if (!updateAvailable) return { text: "Updated", bsStyle: "success" };
    if (lastUpdated >= updateAvailable) return { text: "Updated", bsStyle: "success" };
    return { text: "Pending", bsStyle: "warning" };
}

const SORT_KEYS = {
    camera: "camera",
    port: "port",
    phone: "phone",
    status: "status",
    health: "health",
    lastUpdated: "lastUpdated",
    httpsKeys: "httpsKeys",
    systemType: "systemType",
    yolo: "yolo",
    mp4: "mp4",
    footage: "footage",
    hostname: "hostname",
    timezone: "timezone"
};

function getSortValue(cap, key) {
    const u = cap.updates || {};
    switch (key) {
        case SORT_KEYS.camera:
            return (cap.name || cap.capId || "").toLowerCase();
        case SORT_KEYS.port:
            return cap.port != null ? Number(cap.port) : -1;
        case SORT_KEYS.phone:
            return looksLikePhoneNumber(cap.phoneNumber) ? "yes" : "no";
        case SORT_KEYS.status:
            return (u.status || "").toLowerCase();
        case SORT_KEYS.health:
            return (cap.health || "").toLowerCase();
        case SORT_KEYS.lastUpdated:
            return u.last_updated ? new Date(u.last_updated).getTime() : 0;
        case SORT_KEYS.httpsKeys: {
            const h = getHttpsKeysDisplay(u.https_keys);
            return h.text;
        }
        case SORT_KEYS.systemType:
            return getSystemType(cap.architecture).toLowerCase();
        case SORT_KEYS.yolo:
            return (u.yolo || "").toLowerCase();
        case SORT_KEYS.mp4:
            return u.mediamtx === true ? "yes" : u.mediamtx === false ? "no" : "";
        case SORT_KEYS.footage:
            return u.footage === true ? "yes" : u.footage === false ? "no" : "";
        case SORT_KEYS.hostname:
            return (u.hostname || "").toLowerCase();
        case SORT_KEYS.timezone: {
            const tzDisp = formatTimezoneDisplay(u.timezone);
            return tzDisp ? tzDisp.toLowerCase() : "";
        }
        default:
            return "";
    }
}

export default function CapSoftware(props) {
    const { history, setShowError } = props;
    const [caps, setCaps] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [hidePowerLossError, setHidePowerLossError] = useState(false);
    const [hideBuild, setHideBuild] = useState(false);
    const [schedulingCapId, setSchedulingCapId] = useState(null);
    const [sortKey, setSortKey] = useState(SORT_KEYS.camera);
    const [sortAsc, setSortAsc] = useState(true);

    const loadData = async (forceRefresh = false) => {
        if (!forceRefresh && (hasLoaded || loading)) return;
        setLoading(true);
        try {
            const list = await getCapsAdminOnly(["BUILD", "ACTIVE"]);
            const rawCaps = Array.isArray(list) ? list : [];
            setCaps(rawCaps);
        } catch (error) {
            console.error("Error loading caps:", error);
            setCaps([]);
            if (setShowError) setShowError("Failed to load cap software list.");
        }
        setHasLoaded(true);
        setLoading(false);
    };

    useEffect(() => {
        if (getUser().role !== "ADMIN") {
            history.push("/dashboard");
            return;
        }
        loadData();
    }, []);

    const handleScheduleUpdate = async (cap) => {
        if (!cap || !cap.capId) return;
        setSchedulingCapId(cap.capId);
        const merged = {
            ...(cap.updates || {}),
            status: "UPDATE_AVAILABLE",
            update_available: formatUpdateAvailableDate()
        };
        try {
            await patchCapUpdates(cap.capId, merged);
            setCaps((prev) =>
                prev ? prev.map((c) => (c.capId === cap.capId ? { ...c, updates: merged } : c)) : prev
            );
        } catch (error) {
            console.error("Error scheduling update:", error);
            if (setShowError) setShowError("Failed to schedule update for " + (cap.name || cap.capId) + ".");
        } finally {
            setSchedulingCapId(null);
        }
    };

    const toggleSort = (key) => {
        if (sortKey === key) setSortAsc((prev) => !prev);
        else {
            setSortKey(key);
            setSortAsc(true);
        }
    };

    const sortIcon = (key) => {
        if (sortKey !== key) return <Glyphicon glyph="sort" className="cap-software-sort-icon inactive" />;
        return sortAsc ? (
            <Glyphicon glyph="chevron-up" className="cap-software-sort-icon" />
        ) : (
            <Glyphicon glyph="chevron-down" className="cap-software-sort-icon" />
        );
    };

    const filteredCaps = (caps || [])
        .filter((cap) => {
            if (hidePowerLossError && (cap.health === "POWER_LOSS" || cap.health === "ERROR")) return false;
            if (hideBuild && cap.status === "BUILD") return false;
            return true;
        })
        .filter((cap) => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            const name = (cap.name || "").toLowerCase();
            const hostname = (cap.updates && cap.updates.hostname) ? String(cap.updates.hostname).toLowerCase() : "";
            const tz = (cap.updates && cap.updates.timezone) ? String(cap.updates.timezone).toLowerCase() : "";
            const phoneRaw = cap.phoneNumber != null ? String(cap.phoneNumber).toLowerCase() : "";
            const capId = (cap.capId || "").toLowerCase();
            return name.includes(term) || hostname.includes(term) || tz.includes(term) || phoneRaw.includes(term) || capId.includes(term);
        })
        .sort((a, b) => {
            const valA = getSortValue(a, sortKey);
            const valB = getSortValue(b, sortKey);
            let cmp = 0;
            if (typeof valA === "number" && typeof valB === "number") cmp = valA - valB;
            else cmp = String(valA).localeCompare(String(valB));
            return sortAsc ? cmp : -cmp;
        });

    const adminCapSoftwareHeader = (
        <header className="admin-header">
            <nav className="brand-breadcrumb">
                <Link to="/admin">Admin</Link>
                <span className="brand-breadcrumb-sep">/</span>
                <span className="brand-breadcrumb-current">Cap software</span>
            </nav>
            <h1 className="admin-title">Cap software</h1>
        </header>
    );

    return (
        <div className="cap-software-page admin-page">
            {adminCapSoftwareHeader}
            <div className="cap-software-container">
            <div className="cap-software-search">
                <FormGroup>
                    <div className="cap-software-search-row">
                        <FormControl
                            type="text"
                            placeholder="Search by cap name, hostname, timezone, phone, or cap ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="cap-software-search-input"
                        />
                        <Button
                            bsStyle="default"
                            onClick={() => setSearchTerm("")}
                            className="cap-software-clear-button"
                            title="Clear search"
                        >
                            <Glyphicon glyph="remove" />
                        </Button>
                        <Button
                            bsStyle="default"
                            onClick={() => loadData(true)}
                            disabled={loading}
                            className="cap-software-refresh-button"
                            title="Refresh cap software data"
                        >
                            <Glyphicon glyph="refresh" className={loading ? "glyphicon-spin" : ""} />
                        </Button>
                    </div>
                    <div className="cap-software-filters">
                        <Button
                            bsSize="small"
                            bsStyle={hidePowerLossError ? "primary" : "default"}
                            onClick={() => setHidePowerLossError((prev) => !prev)}
                            title={hidePowerLossError ? "Show POWER_LOSS / ERROR" : "Hide POWER_LOSS / ERROR"}
                        >
                            {hidePowerLossError ? "Showing: no POWER_LOSS/ERROR" : "Hide POWER_LOSS / ERROR"}
                        </Button>
                        <Button
                            bsSize="small"
                            bsStyle={hideBuild ? "primary" : "default"}
                            onClick={() => setHideBuild((prev) => !prev)}
                            title={hideBuild ? "Show BUILD caps" : "Hide BUILD caps"}
                        >
                            {hideBuild ? "Showing: no BUILD" : "Hide BUILD"}
                        </Button>
                    </div>
                </FormGroup>
            </div>
            {hasLoaded && (
                <div className="cap-software-count">
                    Caps: {filteredCaps.length} of {caps ? caps.length : 0}
                </div>
            )}
            <Grid fluid>
                <Row>
                    <Col md={12}>
                        {loading ? (
                            <div className="cap-software-loading">
                                <Glyphicon glyph="refresh" className="glyphicon-spin" style={{ fontSize: "24px", marginRight: "8px" }} />
                                Loading cap software...
                            </div>
                        ) : !caps ? (
                            <div className="cap-software-empty">No data yet.</div>
                        ) : filteredCaps.length === 0 ? (
                            <div className="cap-software-empty">No caps match the search.</div>
                        ) : (
                            <div className="cap-software-table-wrapper">
                                <Table responsive striped bordered hover className="cap-software-table">
                                    <thead>
                                        <tr>
                                            <th className="cap-software-copy-th"></th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.camera)}>Camera {sortIcon(SORT_KEYS.camera)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.port)}>Port{sortIcon(SORT_KEYS.port)}</th>
                                            <th className="cap-software-th-sort cap-software-phone-th" onClick={() => toggleSort(SORT_KEYS.phone)}>Phone{sortIcon(SORT_KEYS.phone)}</th>
                                            <th className="cap-software-th-sort cap-software-status-th" onClick={() => toggleSort(SORT_KEYS.status)}>Status{sortIcon(SORT_KEYS.status)}</th>
                                            <th className="cap-software-th-sort cap-software-health-th" onClick={() => toggleSort(SORT_KEYS.health)}>Health{sortIcon(SORT_KEYS.health)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.lastUpdated)}>Updated{sortIcon(SORT_KEYS.lastUpdated)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.httpsKeys)}>HTTPS{sortIcon(SORT_KEYS.httpsKeys)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.systemType)}>Type{sortIcon(SORT_KEYS.systemType)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.yolo)}>YOLO{sortIcon(SORT_KEYS.yolo)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.mp4)}>MP4 {sortIcon(SORT_KEYS.mp4)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.footage)}>24/7{sortIcon(SORT_KEYS.footage)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.hostname)}>Hostname{sortIcon(SORT_KEYS.hostname)}</th>
                                            <th className="cap-software-th-sort" onClick={() => toggleSort(SORT_KEYS.timezone)}>Timezone{sortIcon(SORT_KEYS.timezone)}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCaps.map((cap) => {
                                            const u = cap.updates || {};
                                            const status = u.status;
                                            const isScheduling = schedulingCapId === cap.capId;
                                            const tzDisp = formatTimezoneDisplay(u.timezone);
                                            const phoneOk = looksLikePhoneNumber(cap.phoneNumber);
                                            return (
                                                <tr key={cap.capId}>
                                                    <td className="cap-software-copy-cell">
                                                        {cap.capId ? (
                                                            <CopyToClipboard text={cap.capId}>
                                                                <Button
                                                                    bsSize="xsmall"
                                                                    bsStyle="default"
                                                                    title="Copy cap ID"
                                                                >
                                                                    <Glyphicon glyph="duplicate" />
                                                                </Button>
                                                            </CopyToClipboard>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td className="cap-software-cap-cell">
                                                        {cap.capId ? (
                                                            <a
                                                                href={`/caps/${cap.capId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={`cap-software-link ${cap.status === "BUILD" ? "cap-software-link-build" : ""}`}
                                                            >
                                                                {cap.name != null ? cap.name : cap.capId}
                                                            </a>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td className="cap-software-port-cell">
                                                        {cap.port != null ? cap.port : "—"}
                                                    </td>
                                                    <td
                                                        className="cap-software-phone-cell"
                                                        title={
                                                            phoneOk
                                                                ? "Phone field looks like a 10- or 11-digit NANP number"
                                                                : "Missing or does not look like a phone number"
                                                        }
                                                    >
                                                        {phoneOk ? "Yes" : "No"}
                                                    </td>
                                                    <td
                                                        className="cap-software-status-cell"
                                                        title={status ? String(status) : undefined}
                                                    >
                                                        {status ? (
                                                            <Label
                                                                bsStyle={getStatusBsStyle(status)}
                                                                className="cap-software-status-label"
                                                            >
                                                                {status === "UPDATE_AVAILABLE" ? "AVAILABLE" : (status || "—")}
                                                            </Label>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td
                                                        className="cap-software-health-cell"
                                                        title={cap.health ? String(cap.health) : undefined}
                                                    >
                                                        {cap.health ? (
                                                            <Label
                                                                bsStyle={getHealthBsStyle(cap.health)}
                                                                className="cap-software-health-label"
                                                            >
                                                                {cap.health}
                                                            </Label>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td className="cap-software-date-cell">{formatDateOnly(u.last_updated)}</td>
                                                    <td>
                                                        {(() => {
                                                            const h = getHttpsKeysDisplay(u.https_keys);
                                                            return h.text === "—" ? "—" : <Label bsStyle={h.bsStyle}>{h.text}</Label>;
                                                        })()}
                                                    </td>
                                                    <td>{getSystemType(cap.architecture)}</td>
                                                    <td>{u.yolo != null ? u.yolo : "—"}</td>
                                                    <td>
                                                        {u.mediamtx === true ? "Yes" : u.mediamtx === false ? "No" : "—"}
                                                    </td>
                                                    <td>
                                                        {u.footage === true ? "Yes" : u.footage === false ? "No" : "—"}
                                                    </td>
                                                    <td>{u.hostname != null ? u.hostname : "—"}</td>
                                                    <td
                                                        className="cap-software-tz-cell"
                                                        title={u.timezone ? String(u.timezone) : undefined}
                                                    >
                                                        {tzDisp != null ? tzDisp : "—"}
                                                    </td>
                                                    <td className="cap-software-action-cell">
                                                        <Button
                                                            bsSize="xsmall"
                                                            bsStyle={status === "UPDATE_AVAILABLE" ? "warning" : "primary"}
                                                            disabled={isScheduling || status === "UPDATE_AVAILABLE"}
                                                            onClick={() => handleScheduleUpdate(cap)}
                                                            title={status === "UPDATE_AVAILABLE" ? "Update already scheduled" : "Set status to UPDATE_AVAILABLE and update_available to now"}
                                                        >
                                                            {isScheduling ? (
                                                                <Glyphicon glyph="refresh" className="glyphicon-spin" />
                                                            ) : status === "UPDATE_AVAILABLE" ? (
                                                                "Scheduled"
                                                            ) : (
                                                                "Schedule"
                                                            )}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Col>
                </Row>
            </Grid>
            </div>
        </div>
    );
}
